<template>
  <div class="bg-white d-flex flex-nowrap w-100 overflow-auto">
    <div class="p-1 w-100" v-if="loadingFormData">
      <SkeletonLoading
        :count="1"
        :height="130"
        :fullWidth="true"
      />
      <SkeletonLoading
        :count="1"
        :height="130"
        :fullWidth="true"
      />
    </div>
    <div v-if="!loadingFormData" class="w-100">
      <div class="d-flex justify-content-between align-items-center">
        <h5 class="mt-1 ml-1 mb-1 lead">{{ crisisForm.name }} -
          {{ crisisForm.reference_number }}</h5>

        <!-- Icons data-table -->
        <div class="icons">
          <div class="d-flex align-items-center">
                <span
                  draggable="true"
                  @dragstart="onDragStartMasterData($event)"
                  @dragend="onDragEnd"
                  :title="$t('operational_security.crisis_form.master_data')"
                  class="mr-1 cursor-pointer d-inline-flex"
                >
                  <feather-icon
                    icon="DatabaseIcon"
                    size="16"
                    class="icon-draggable"
                    v-b-tooltip.hover.bottom.viewport="`${$t('operational_security.crisis_form.master_data')}`"
                  />
                </span>
            <span
              draggable="true"
              @dragstart="onDragStartAllocationsByRegion($event)"
              @dragend="onDragEnd"
              :title="$t('operational_security.crisis_form.allocations_by_region')"
              class="mr-1 cursor-pointer d-inline-flex"
            >
                  <feather-icon
                    icon="MapPinIcon"
                    size="16"
                    class="icon-draggable"
                    v-b-tooltip.hover.bottom.viewport="`${$t('operational_security.crisis_form.allocations_by_region')}`"
                  />
                </span>
            <span
              draggable="true"
              @dragstart="onDragStartAllocationsByMunicipality($event)"
              @dragend="onDragEnd"
              :title="$t('operational_security.crisis_form.allocations_by_municipality')"
              class="mr-1 cursor-pointer d-inline-flex"
            >
                  <feather-icon
                    icon="AlertOctagonIcon"
                    size="16"
                    class="icon-draggable"
                    v-b-tooltip.hover.bottom.viewport="`${$t('operational_security.crisis_form.allocations_by_municipality')}`"
                  />
                </span>
            <span
              draggable="true"
              @dragstart="onDragStartAllocationsByFacilities($event)"
              @dragend="onDragEnd"
              :title="$t('operational_security.crisis_form.allocations_by_facility')"
              class="mr-1 cursor-pointer d-inline-flex"
            >
                  <feather-icon
                    icon="HomeIcon"
                    size="16"
                    class="icon-draggable"
                    v-b-tooltip.hover.bottom.viewport="`${$t('operational_security.crisis_form.allocations_by_facility')}`"
                  />
                </span>
            <span
              draggable="true"
              @dragstart="onDragStartEmployeeInformationByFacility($event)"
              @dragend="onDragEnd"
              :title="$t('operational_security.crisis_form.employee_information_by_facility')"
              class="mr-1 cursor-pointer d-inline-flex"
            >
                  <feather-icon
                    icon="UsersIcon"
                    size="16"
                    class="icon-draggable"
                    v-b-tooltip.hover.bottom.viewport="`${$t('operational_security.crisis_form.employee_information_by_facility')}`"
                  />
                </span>
          </div>
        </div>
        <!-- -->
      </div>
      <template v-if="operationalSecurityCrisis.units.length">
        <units-crisis
          loadedSource="slot"
          :form-status="formStatus"
          :key="unitsKey"
          :last-status="lastStatus"
          :operational-security-crisis="operationalSecurityCrisis"
          :show-options-by-status="showOptionsByStatus"
          :user="userStore"
          :is-read-only="isReadOnly"
          :user-capabilities="userCapabilities"
          @build-data-from-response="buildOperationalSecurityCrisisFromResponse"
          @refresh-units="unitsKey++"
        />
      </template>
      <b-alert
        v-else
        show
        variant="warning"
        class="p-1 mb-0"
      >
        {{ $t('operational_security.crisis_form.emptyUnits') }}
      </b-alert>
    </div>
  </div>
</template>

<script>
import {computed, onMounted, ref, watch} from "@vue/composition-api";
import store from "@/store";
import axios from "@/libs/axios";
import {required} from '@validations';
import formValidation from '@core/comp-functions/forms/form-validation';
import {useToast} from "vue-toastification/composition";
import SkeletonLoading from "@/components/SkeletonLoading.vue";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import UnitsCrisis
  from "@/views/operational_security/crisis_form/components/UnitsCrisis.vue";
import vSelect from "vue-select";
import LayoutApi from "@/libs/LayoutApi";
import {SLOT_TYPES} from "@/config/layoutConfig";
import {
  useCrisisResponsePatch
} from "@/views/operational_security/crisis_form/components/composables/response-patch/useCrisisResponsePatch";

export default {
  components: {
    ToastificationContent,
    UnitsCrisis,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    SkeletonLoading
  },
  props: {
    data: {
      type: Object,
    },
    slotId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      required
    }
  },
  setup(props) {
    const layoutApi = new LayoutApi(store);

    const loadingFormData = ref(true);
    const crisisForm = ref(props.data.item);

    const loadCrisisForm = async (item) => {
      if (!item?.id) return;

      crisisForm.value = item;
      loadingFormData.value = true;
      await getCrisisForm(item.id);
      unitsKey.value++;
    };

    onMounted(() => {
      loadCrisisForm(props.data.item);
    });

    watch(
      () => props.data?.item?.id,
      (newId, oldId) => {
        if (newId && newId !== oldId) {
          loadCrisisForm(props.data.item);
        }
      }
    );

    const resetData = () => {
      operationalSecurityCrisis.value = {
        id: null,
        name: null,
        description: null,
        to_list_id: null,
        cc_list_id: null,
        bcc_list_id: null,
        supervisors: [],
        units: []
      }
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm
    } = formValidation(resetData);

    const toast = useToast();

    const crisisFormData = ref(null);
    const distributionsList = ref([]);
    const loading = ref(false);
    const loadingSearch = ref(false);
    const lastLogDate = ref(null);
    const logsSectionRef = ref(null);
    const unitsKey = ref(1)

    const isFormDisabled = ref(false);

    const operationalSecurityCrisis = ref({
      id: null,
      name: null,
      level: null,
      description: null,
      csis_days_in_field: null,
      csis_total_volunteers: null,
      to_list_id: null,
      cc_list_id: null,
      bcc_list_id: null,
      id_tenant: null,
      supervisors: [],
      units: [],
      units_users_rh: [],
      last_status: null,
      validations: []
    });

    const showModalUnitsList = ref(false);
    const user = ref([]);
    const usersOptions = ref([]);

    //COMPUTED
    const formStatus = computed(() => ({
      withoutValue: (!lastStatus.value),
      inProgress: (lastStatus.value?.status.description === 'in_progress'),
      isRejected: (lastStatus.value?.status.description === 'rejected'),
      isApproved: (lastStatus.value?.status.description === 'completed'),
      isReview: (lastStatus.value?.status.description === 'review'),
      isReopen: (lastStatus.value?.status.description === 'reopened'),
      isCanceled: (lastStatus.value?.status.description === 'canceled')
    }));

    const tenantOptions = computed(() => store.getters['tenant/getSelectedItems']);
    const userStore = computed(() => store.getters["user/getUser"]);
    const userCapabilities = computed(() => {
      const isOwner = crisisFormData.value?.owner && crisisFormData.value?.owner.id === userStore.value.id;
      const supervisorProfile = userStore.value.profile.type === 'supervisor_monitoreo';
      const isFormSupervisor = crisisFormData.value?.supervisors.some(sv => sv.id === userStore.value.id);
      const canUpdateFormConfig = isOwner || (isFormSupervisor && supervisorProfile) || userStore.value.is_master_admin;

      return {
        canUpdateFormConfig: crisisFormData.value ? canUpdateFormConfig : true,
        isOwner: crisisFormData.value ? isOwner : true,
        supervisorProfile,
        isFormSupervisor,
      };
    });
    const isReadOnly = computed(() => !userCapabilities.value.isFormSupervisor && !userCapabilities.value.isOwner);
    const isDark = computed(() => store.state.appConfig.layout.skin === 'dark');
    const lastStatus = computed(() => operationalSecurityCrisis.value.last_status);
    const showOptionsByStatus = computed(() => (formStatus.value.inProgress || formStatus.value.isReopen || formStatus.value.isRejected || formStatus.value.withoutValue));
    const showSaveButtons = computed(() => {
      if (operationalSecurityCrisis.value.id) {
        if (!lastStatus.value) {
          return true;
        }

        // return (showOptionsByStatus.value && (userStore.value.id === lastStatus.value.id_user));
        return (showOptionsByStatus.value);
      }

      return showOptionsByStatus.value;
    });

    // ===== Start: Crisis installation response patch flow =====
    // Reuses the main crisis form patch pipeline so slot units keep the latest data.
    const {
      buildOperationalSecurityCrisisFromResponse,
    } = useCrisisResponsePatch({
      operationalSecurityCrisis,
      tenantChanged: () => {},
    });
    // ===== End: Crisis installation response patch flow =====

    const getCrisisForm = async (crisisFormId) => {
      loadingSearch.value = true;

      await axios.get(`/v1/operational-security-crisis/${crisisFormId}`)
        .then(response => {
          const {data} = response;
          crisisFormData.value = data.data;
          loadingFormData.value = false;
          buildOperationalSecurityCrisisFromResponse(data.data);
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          });
        })
        .finally(() => {
          loadingSearch.value = false;
        });
    }

    const buildGeneralSettingsPayload = () => {
      const {
        id,
        name,
        description,
        level,
        csis_days_in_field,
        csis_total_volunteers,
        to_list_id,
        cc_list_id,
        bcc_list_id,
        reference_number,
        id_tenant,
        supervisors
      } = operationalSecurityCrisis.value;

      return {
        id,
        name,
        description,
        level,
        csis_days_in_field,
        csis_total_volunteers,
        to_list_id,
        cc_list_id,
        bcc_list_id,
        reference_number,
        id_tenant,
        supervisors
      };
    }

    const save = () => {
      loading.value = true;

      const isUpdate = Boolean(operationalSecurityCrisis.value.id);
      const method = isUpdate ? 'put' : 'post';
      const url = isUpdate ? `/v1/operational-security-crisis/${operationalSecurityCrisis.value.id}` : '/v1/operational-security-crisis';
      const payload = isUpdate ? buildGeneralSettingsPayload() : operationalSecurityCrisis.value;

      axios[method](url, payload)
        .then(response => {
          const {data} = response;

          toast({
            component: ToastificationContent,
            props: {
              title: data.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          });

          buildOperationalSecurityCrisisFromResponse(data.data, {mode: isUpdate ? 'patch' : 'replace'});

          if (data.data && data.data.id) {
            logsSectionRef.value?.refresh(1);
          }
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          });
        })
        .finally(() => {
          loading.value = false;
        });
    }

    const onDragEnd = () => {
      store.dispatch('grid/resetSlotSelection');
    };

    const onDragStartMasterData = () => {
      layoutApi.setSlotData(SLOT_TYPES.MASTER_DATA_TABLE, 'Master Data', crisisForm.value);
    };

    const onDragStartAllocationsByRegion = () => {
      layoutApi.setSlotData(SLOT_TYPES.ALLOCATIONS_BY_REGION_TABLE, 'Allocations by region', crisisForm.value);
    };

    const onDragStartAllocationsByMunicipality = () => {
      layoutApi.setSlotData(SLOT_TYPES.ALLOCATIONS_BY_MUNICIPALITY_TABLE, 'Allocations by municipality', crisisForm.value);
    };

    const onDragStartAllocationsByFacilities = () => {
      layoutApi.setSlotData(SLOT_TYPES.ALLOCATIONS_BY_FACILITIES_TABLE, 'Allocations by facilities', crisisForm.value);
    };

    const onDragStartEmployeeInformationByFacility = () => {
      layoutApi.setSlotData(SLOT_TYPES.EMPLOYEE_INFORMATION_BY_FACILITY_TABLE, 'Employee information by facility', crisisForm.value);
    };

    return {
      isFormDisabled,
      getValidationState,
      resetForm,
      refFormObserver,

      distributionsList,
      loading,
      loadingSearch,
      logsSectionRef,
      operationalSecurityCrisis,
      showModalUnitsList,
      user,
      usersOptions,
      unitsKey,

      formStatus,
      loadingFormData,

      isDark,
      lastLogDate,
      lastStatus,
      showOptionsByStatus,
      showSaveButtons,
      tenantOptions,
      userStore,
      crisisFormData,
      userCapabilities,
      isReadOnly,
      crisisForm,

      buildOperationalSecurityCrisisFromResponse,
      save,

      onDragEnd,
      onDragStartMasterData,
      onDragStartAllocationsByRegion,
      onDragStartAllocationsByMunicipality,
      onDragStartAllocationsByFacilities,
      onDragStartEmployeeInformationByFacility,
    }
  }
}
</script>
